/* Merchant panel › Returns & RVP › RVP QC Parameters — READ-ONLY.
   Shows the logged-in merchant's own QC config: QC type + active checks
   (Standard list / Reason-based matrix / Non-QC notice). View-only;
   "Request a change" opens a prefilled email to rvpqc_setup.change@prozo.com. */
import { useEffect, useState } from 'react'
import {
  Box, Typography, Chip, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import withLayout from '../../../hocs/Layout'
import PageTitle from '../../../common/PageTitle'
import Loader from '../../../common/Loader'
import { pageContainerSx } from '../../../styles/patterns'
import { getGeneric } from '../../../common/constants'
import { getMerchant } from '../../../helpers/ReduxHelpers'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import { NAVY, SUBTLE, BORDER, WHITE, SUCCESS, ERROR, PRIMARY_BLUE, WARNING, withOpacity } from '../../../styles/foundation/colors'

const CHANGE_EMAIL = 'rvpqc_setup.change@prozo.com'
const MODE_LABEL = { STANDARD: 'Standard QC', PARAM: 'Reason-based QC', NONE: 'Non-QC' }
const MODE_COLOR = { STANDARD: WARNING, PARAM: '#7c4dff', NONE: SUBTLE }

const cardSx = { bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', p: 2, mb: 2 }
const chipSx = (color) => ({
  fontFamily: FONT, fontSize: 11, fontWeight: WEIGHT.bold, height: 22,
  bgcolor: withOpacity(color, 0.12), color, border: `1px solid ${withOpacity(color, 0.3)}`,
  '& .MuiChip-label': { px: 1 },
})
const monoSx = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5, fontWeight: WEIGHT.semibold, color: NAVY }
const thSx = { fontFamily: FONT, fontSize: 10.5, fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.03em', bgcolor: withOpacity(SUBTLE, 0.04), borderColor: BORDER, py: 1 }
const tdSx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, borderColor: BORDER, py: 1 }

function InfoStrip({ children, color = PRIMARY_BLUE }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.25, borderRadius: '10px', bgcolor: withOpacity(color, 0.06), border: `1px solid ${withOpacity(color, 0.22)}` }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color, mt: '1px', flexShrink: 0 }} />
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, lineHeight: 1.55 }}>{children}</Typography>
    </Box>
  )
}

function EmptyState({ title, sub }) {
  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>{title}</Typography>
      {sub && <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5 }}>{sub}</Typography>}
    </Box>
  )
}

function RvpWithQc() {
  const merchant = getMerchant()
  const merchantOid = merchant?.id
  const merchantName = merchant?.name || 'Your account'

  const [cfg, setCfg] = useState(null)
  const [params, setParams] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!merchantOid) { setLoading(false); return }
    setLoading(true); setError(false)
    Promise.all([
      getGeneric(`config/merchant/${merchantOid}/qc-return`),
      getGeneric('qc-parameter-master', { activeOnly: true }),
      getGeneric(`config/merchant/${merchantOid}`),
    ])
      .then(([cfgRes, pRes, mRes]) => {
        setCfg(cfgRes?.data && typeof cfgRes.data === 'object' ? cfgRes.data : {})
        const pd = pRes?.data
        setParams(Array.isArray(pd) ? pd : (Array.isArray(pd?.result) ? pd.result : (Array.isArray(pd?.data) ? pd.data : [])))
        setHidden(mRes?.data?.showRvpQcToMerchant === false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [merchantOid])

  const descOf = (code) => params.find((p) => p.code === code)?.description || ''
  const mode = cfg?.mode || (merchantOid && !loading ? 'NONE' : null)

  const requestChange = () => {
    const subject = `RVP-QC change request — ${merchantName}`
    const body = `Merchant: ${merchantName}\nMerchant OID: ${merchantOid || ''}\nCurrent QC type: ${mode ? (MODE_LABEL[mode] || mode) : '—'}\n\nRequested change:\n`
    window.location.href = `mailto:${CHANGE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Your reverse-pickup quality checks (view only)">RVP QC Parameters</PageTitle>

      {/* Header card */}
      <Box sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: PRIMARY_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <VerifiedOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY, lineHeight: 1.2 }}>Reverse Pickup — Quality Checks</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>{merchantName}</Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip size="small" label="View only" sx={chipSx(SUBTLE)} />
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={requestChange}
              sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', fontWeight: WEIGHT.semibold, borderColor: BORDER, color: NAVY, borderRadius: '9px', '&:hover': { borderColor: PRIMARY_BLUE, bgcolor: withOpacity(PRIMARY_BLUE, 0.05) } }}>
              Request a change
            </Button>
          </Box>
        </Box>
        <InfoStrip>
          <b>This page is view only.</b> Your RVP-with-QC setup is configured by your Proship admin and applies{' '}
          <b>globally to all your reverse pickups</b>. Use <b>Request a change</b> to raise a request — it triggers an{' '}
          auto-email to <Box component="span" sx={monoSx}>{CHANGE_EMAIL}</Box> and is logged, because QC parameters are <b>claims-impacting</b>.
        </InfoStrip>
      </Box>

      {loading && <Loader />}

      {!merchantOid && (
        <Box sx={cardSx}><EmptyState title="No merchant in session" sub="Sign in as a merchant to view your QC configuration." /></Box>
      )}
      {merchantOid && error && (
        <Box sx={cardSx}><InfoStrip color={ERROR}>Couldn't load your QC configuration. Please retry.</InfoStrip></Box>
      )}
      {merchantOid && !loading && !error && hidden && (
        <Box sx={cardSx}><EmptyState title="Not enabled" sub="RVP-with-QC isn't available in the merchant panel for your account. Contact your Proship admin if you need access." /></Box>
      )}

      {merchantOid && !loading && !error && !hidden && (
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>QC type</Typography>
            <Box sx={{ ml: 'auto' }}><Chip size="small" label={MODE_LABEL[mode] || 'Non-QC'} sx={chipSx(MODE_COLOR[mode] || SUBTLE)} /></Box>
          </Box>

          {mode === 'STANDARD' && <StandardChecks list={cfg?.standardParameters || []} descOf={descOf} />}
          {mode === 'PARAM' && <ReasonMatrixReadOnly reasonMap={cfg?.reasonMap || []} />}
          {(mode === 'NONE' || !mode) && (
            <InfoStrip>No quality checks are configured. Your reverse pickups are accepted <b>without any doorstep QC</b>.</InfoStrip>
          )}
        </Box>
      )}
    </Box>
  )
}

/* Standard: fixed checklist — check + Mandatory/Optional chip */
function StandardChecks({ list, descOf }) {
  if (!list.length) return <EmptyState title="No checks selected" />
  return (
    <>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 1 }}>
        These checks run on <b style={{ color: NAVY }}>every</b> return.
      </Typography>
      <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>Check</TableCell>
              <TableCell align="center" sx={{ ...thSx, width: 160 }}>Requirement</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((p) => (
              <TableRow key={p.code}>
                <TableCell sx={tdSx}>
                  <Box component="span" sx={monoSx}>{p.code}</Box>
                  {descOf(p.code) && <Box component="span" sx={{ color: SUBTLE, ml: 1, fontSize: 11.5 }}>{descOf(p.code)}</Box>}
                </TableCell>
                <TableCell align="center" sx={tdSx}>
                  <Chip size="small" label={p.mandatory ? 'Mandatory' : 'Optional'} sx={chipSx(p.mandatory ? ERROR : SUBTLE)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

/* Reason-based: reason × check matrix, Yes/No chips + Mandatory-checks column */
function ReasonMatrixReadOnly({ reasonMap }) {
  if (!reasonMap.length) return <EmptyState title="No return reasons configured" />
  const cols = [...new Set(reasonMap.flatMap((r) => (r.params || []).map((p) => p.code)))]
  const has = (r, code) => (r.params || []).some((p) => p.code === code)
  const mand = (r) => (r.params || []).filter((p) => p.mandatory).map((p) => p.code)
  return (
    <>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 1 }}>
        The <b style={{ color: NAVY }}>verbatim return reason</b> decides which checks run.
      </Typography>
      <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, minWidth: 200 }}>Return reason</TableCell>
              {cols.map((c) => <TableCell key={c} align="center" sx={thSx}><Box component="span" sx={monoSx}>{c}</Box></TableCell>)}
              <TableCell sx={{ ...thSx, minWidth: 170 }}>Mandatory checks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reasonMap.map((r, i) => (
              <TableRow key={i}>
                <TableCell sx={tdSx}>{r.returnReason}</TableCell>
                {cols.map((c) => (
                  <TableCell key={c} align="center" sx={tdSx}>
                    <Chip size="small" label={has(r, c) ? 'Yes' : 'No'} sx={chipSx(has(r, c) ? SUCCESS : SUBTLE)} />
                  </TableCell>
                ))}
                <TableCell sx={tdSx}>
                  {mand(r).length
                    ? <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{mand(r).map((x) => <Chip key={x} size="small" label={x} sx={{ ...chipSx(ERROR), ...monoSx, color: ERROR }} />)}</Box>
                    : <Chip size="small" label="none" sx={chipSx(SUBTLE)} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 3, mt: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: SUBTLE }}><b style={{ color: SUCCESS }}>Yes</b> = check runs for this reason</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11, color: SUBTLE }}><b style={{ color: ERROR }}>Mandatory</b> check missing → shipment creation fails</Typography>
      </Box>
    </>
  )
}

export default withLayout(RvpWithQc)
